import React, { useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen, faSpinner, faTrash } from '@fortawesome/free-solid-svg-icons'
import PhotoGalleryFrame, { photoGallerySafeIndex } from '../PhotoGalleryFrame'

type FleetPhotoReplaceFrameProps = {
  images: string[]
  index: number
  onIndexChange: (index: number) => void
  alt: string
  emptyLabel: string
  canEdit: boolean
  disabled?: boolean
  busy?: boolean
  /** Appends selected file(s) to the photo set. */
  onAdd: (files: FileList | null) => void | Promise<void>
  /** Removes the photo at the given index after confirm. */
  onDelete: (index: number) => void | Promise<void>
  className?: string
}

/** Gallery with pencil (add) and trash (delete current, with confirm). */
export const FleetPhotoReplaceFrame: React.FC<FleetPhotoReplaceFrameProps> = ({
  images,
  index,
  onIndexChange,
  alt,
  emptyLabel,
  canEdit,
  disabled = false,
  busy = false,
  onAdd,
  onDelete,
  className = '',
}) => {
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const locked = disabled || busy || deleting
  const safeIndex = photoGallerySafeIndex(index, images.length)
  const hasCurrent = images.length > 0 && Boolean(images[safeIndex])

  const confirmDelete = async () => {
    if (!hasCurrent) return
    setDeleting(true)
    try {
      await onDelete(safeIndex)
      setConfirmDeleteOpen(false)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <div className={`relative w-full ${className}`}>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept="image/*"
        multiple
        className="sr-only"
        disabled={locked || !canEdit}
        onChange={(e) => {
          void onAdd(e.target.files)
          e.currentTarget.value = ''
        }}
      />
      <PhotoGalleryFrame
        images={images}
        index={index}
        onIndexChange={onIndexChange}
        alt={alt}
        variant="wide"
        emptyLabel={emptyLabel}
      />
      {canEdit ? (
        <>
          {hasCurrent ? (
            <button
              type="button"
              className="btn btn-circle btn-sm absolute left-2 top-2 z-30 bg-base-100/95 border-base-300 text-error shadow"
              disabled={locked}
              title="Delete photo"
              aria-label={`Delete current ${alt} photo`}
              onClick={() => {
                if (locked) return
                setConfirmDeleteOpen(true)
              }}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          ) : null}
          <button
            type="button"
            className="btn btn-circle btn-sm absolute right-2 top-2 z-30 bg-base-100/95 border-base-300 shadow"
            disabled={locked}
            title="Add photos"
            aria-label={`Add ${alt} photos`}
            onClick={() => {
              if (locked) return
              inputRef.current?.click()
            }}
          >
            <FontAwesomeIcon icon={busy ? faSpinner : faPen} className={busy ? 'animate-spin' : ''} />
          </button>
        </>
      ) : null}

      {confirmDeleteOpen
        ? createPortal(
            <div
              className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/50 p-4"
              onClick={() => {
                if (!deleting) setConfirmDeleteOpen(false)
              }}
              role="presentation"
            >
              <div
                className="w-full max-w-sm rounded-box bg-base-100 p-5 shadow-xl"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-label="Confirm delete photo"
              >
                <h3 className="font-semibold text-lg">Delete photo?</h3>
                <p className="mt-2 text-sm text-base-content/70">
                  This removes the current picture. This cannot be undone.
                </p>
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={deleting}
                    onClick={() => setConfirmDeleteOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-error btn-sm"
                    disabled={deleting}
                    onClick={() => void confirmDelete()}
                  >
                    <FontAwesomeIcon
                      icon={deleting ? faSpinner : faTrash}
                      className={deleting ? 'animate-spin' : ''}
                    />
                    {deleting ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  )
}

export default FleetPhotoReplaceFrame
